import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Users, X, Plus, CheckCircle, LogIn, PenSquare } from "lucide-react";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const JoinTeamModal = () => {
  const { user, token } = useAuth();
  const { t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [skillInput, setSkillInput] = useState("");
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    last_degree: "",
    field_of_study: "",
    skills: [],
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const addSkill = () => {
    if (skillInput.trim() && !formData.skills.includes(skillInput.trim())) {
      setFormData((prev) => ({
        ...prev,
        skills: [...prev.skills, skillInput.trim()],
      }));
      setSkillInput("");
    }
  };

  const removeSkill = (skillToRemove) => {
    setFormData((prev) => ({
      ...prev,
      skills: prev.skills.filter((skill) => skill !== skillToRemove),
    }));
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addSkill();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.skills.length === 0) {
      toast.error("Veuillez ajouter au moins une compétence");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API}/team/apply`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        setSubmitted(true);
        toast.success("Candidature envoyée !");
      } else {
        toast.error(data.detail || "Erreur lors de l'envoi");
      }
    } catch (error) {
      console.error("Submit error:", error);
      toast.error("Erreur lors de l'envoi");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      full_name: "",
      email: "",
      last_degree: "",
      field_of_study: "",
      skills: [],
    });
    setSkillInput("");
    setSubmitted(false);
  };

  const handleOpenChange = (open) => {
    setIsOpen(open);
    if (!open) {
      setTimeout(resetForm, 300);
    }
  };

  // Si l'utilisateur est déjà éditeur ou admin, afficher le bouton pour écrire un article
  if (user && (user.role === "editor" || user.role === "admin")) {
    return (
      <Link to="/editor/new-article">
        <Button
          size="lg"
          variant="outline"
          className="rounded-full px-6 sm:px-8 py-5 sm:py-6 border-primary/30 hover:bg-primary/10"
          data-testid="write-article-btn"
        >
          <PenSquare className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
          Écrire un article
        </Button>
      </Link>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button
          size="lg"
          variant="outline"
          className="rounded-full px-6 sm:px-8 py-5 sm:py-6 border-primary/30 hover:bg-primary/10"
          data-testid="join-team-btn"
        >
          <Users className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
          {t("team.joinTeam")}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">
            {submitted ? t("team.submitted") : t("team.joinTeam")}
          </DialogTitle>
        </DialogHeader>

        <AnimatePresence mode="wait">
          {!user ? (
            <motion.div
              key="login-required"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="text-center py-6"
            >
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <LogIn className="h-8 w-8 text-primary" />
              </div>
              <p className="text-muted-foreground mb-4">
                {t("team.loginRequired")}
              </p>
              <Link to="/login">
                <Button className="rounded-full">
                  {t("nav.login")}
                </Button>
              </Link>
            </motion.div>
          ) : submitted ? (
            <motion.div
              key="success"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="text-center py-6"
            >
              <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-green-500" />
              </div>
              <p className="text-muted-foreground mb-4">
                {t("team.successMsg")}
              </p>
              <Button onClick={() => handleOpenChange(false)} className="rounded-full">
                {t("common.close")}
              </Button>
            </motion.div>
          ) : (
            <motion.form
              key="form"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onSubmit={handleSubmit}
              className="space-y-4"
            >
              <div className="space-y-2">
                <Label htmlFor="full_name">{t("team.fullName")} *</Label>
                <Input
                  id="full_name"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  placeholder={t("team.fullNamePlaceholder")}
                  required
                  data-testid="join-fullname"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">{t("contact.email")} *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="votre@email.com"
                  required
                  data-testid="join-email"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="last_degree">{t("team.degree")} *</Label>
                <Input
                  id="last_degree"
                  name="last_degree"
                  value={formData.last_degree}
                  onChange={handleChange}
                  placeholder={t("team.degreePlaceholder")}
                  required
                  data-testid="join-degree"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="field_of_study">{t("team.fieldOfStudy")} *</Label>
                <Input
                  id="field_of_study"
                  name="field_of_study"
                  value={formData.field_of_study}
                  onChange={handleChange}
                  placeholder={t("team.fieldPlaceholder")}
                  required
                  data-testid="join-field"
                />
              </div>

              <div className="space-y-2">
                <Label>{t("team.skills")} *</Label>
                <div className="flex gap-2">
                  <Input
                    value={skillInput}
                    onChange={(e) => setSkillInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={t("team.skillsPlaceholder")}
                    data-testid="join-skill-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={addSkill}
                    data-testid="join-add-skill"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                {formData.skills.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.skills.map((skill) => (
                      <Badge
                        key={skill}
                        variant="secondary"
                        className="pl-3 pr-1 py-1 flex items-center gap-1"
                      >
                        {skill}
                        <button
                          type="button"
                          onClick={() => removeSkill(skill)}
                          className="ml-1 hover:bg-secondary-foreground/20 rounded-full p-0.5"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
                <p className="text-xs text-muted-foreground">
                  {t("team.skillsHint")}
                </p>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full rounded-full"
                data-testid="join-submit"
              >
                {loading ? t("common.loading") : t("team.submit")}
              </Button>
            </motion.form>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
};

export default JoinTeamModal;
